import { IRbacPermission } from '../apis/__shared/rbac.interfaces';

/**
 * Get permission object for a specific module and submodule
 * @param rbac - RBAC data object
 * @param module - Module name (e.g., 'students', 'staff')
 * @param submodule - Submodule name (e.g., 'studentList', 'attendance')
 * @returns Permission object or null if not found
 */
export const getPermission = (
  rbac: any,
  module: string,
  submodule: string
): IRbacPermission | null => {
  if (!rbac || !module || !submodule) {
    return null;
  }

  const moduleData = rbac[module];
  if (!moduleData) {
    return null;
  }

  const submoduleData = moduleData[submodule];
  if (!submoduleData) {
    return null;
  }

  return submoduleData as IRbacPermission;
};

/**
 * Check if user has read permission for a module/submodule
 * @param rbac - RBAC data object
 * @param module - Module name
 * @param submodule - Submodule name
 * @returns true if user has read permission, false otherwise
 */
export const hasReadPermission = (
  rbac: any,
  module: string,
  submodule: string
): boolean => {
  const permission = getPermission(rbac, module, submodule);
  return permission?.read === true;
};

/**
 * Check if user has write permission for a module/submodule
 * @param rbac - RBAC data object
 * @param module - Module name
 * @param submodule - Submodule name
 * @returns true if user has write permission, false otherwise
 */
export const hasWritePermission = (
  rbac: any,
  module: string,
  submodule: string
): boolean => {
  const permission = getPermission(rbac, module, submodule);
  return permission?.write === true;
};

/**
 * Check if user has delete permission for a module/submodule
 * @param rbac - RBAC data object
 * @param module - Module name
 * @param submodule - Submodule name
 * @returns true if user has delete permission, false otherwise
 */
export const hasDeletePermission = (
  rbac: any,
  module: string,
  submodule: string
): boolean => {
  const permission = getPermission(rbac, module, submodule);
  return permission?.delete === true;
};

/**
 * Check if user has any permission (read, write, or delete) for a module/submodule
 * @param rbac - RBAC data object
 * @param module - Module name
 * @param submodule - Submodule name
 * @returns true if user has any permission, false otherwise
 */
export const hasAnyPermission = (
  rbac: any,
  module: string,
  submodule: string
): boolean => {
  const permission = getPermission(rbac, module, submodule);
  if (!permission) {
    return false;
  }
  return (
    permission.read === true ||
    permission.write === true ||
    permission.delete === true
  );
};

/**
 * Check if user has read permission for any submodule in a module
 * @param rbac - RBAC data object
 * @param module - Module name
 * @returns true if user has read permission for any submodule, false otherwise
 */
export const hasAnySubmoduleReadPermission = (
  rbac: any,
  module: string
): boolean => {
  if (!rbac || !module) {
    return false;
  }

  const moduleData = rbac[module];
  if (!moduleData) {
    return false;
  }

  return Object.values(moduleData).some(
    (submodule: any) => submodule?.read === true
  );
};

/**
 * Check permission before API call and throw error if not authorized
 * @param rbac - RBAC data object
 * @param module - Module name
 * @param submodule - Submodule name
 * @param permissionType - Type of permission needed ('write' for POST/PUT/PATCH, 'delete' for DELETE)
 * @throws Error if permission check fails
 */
export const checkApiPermission = (
  rbac: any,
  module: string,
  submodule: string,
  permissionType: 'write' | 'delete'
): void => {
  if (!rbac) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('RBAC data not available, skipping permission check');
    }
    return;
  }

  const hasPermission =
    permissionType === 'write'
      ? hasWritePermission(rbac, module, submodule)
      : hasDeletePermission(rbac, module, submodule);

  if (!hasPermission) {
    const errorMessage = `Unauthorized: ${permissionType} permission required for ${module}.${submodule}`;
    if (process.env.NODE_ENV === 'development') {
      console.error('API Permission Check Failed:', errorMessage);
    }
    throw new Error(errorMessage);
  }
};
